import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { listSavedSearches, deleteSavedSearch } from '../api/savedSearches';
import './SavedSearches.css';

/**
 * Saved Searches / Alerts page.
 * Minimal UI: list + one-tap delete + "View results" (re-runs the search).
 * Reads `db.saved_searches` via /api/saved-searches; delete uses /{id}.
 */
export default function SavedSearches() {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const goBack = useSmartBack('/account');
  const [searches, setSearches] = useState([]);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSavedSearches();
      setSearches(res?.searches || []);
      if (res?.limit) setLimit(res.limit);
    } catch (e) {
      toast.error(t('savedSearches.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteSavedSearch(id);
      setSearches(prev => prev.filter(s => s.id !== id));
      toast.success(t('savedSearches.deleted'));
    } catch (e) {
      toast.error(t('savedSearches.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const runSearch = (savedSearch) => {
    const filters = savedSearch.filters || {};
    const params = new URLSearchParams();
    if (filters.query) params.set('search', filters.query);
    if (filters.category) params.set('category', filters.category);
    if (filters.location) params.set('location', filters.location);
    if (filters.min_price != null) params.set('min_price', String(filters.min_price));
    if (filters.max_price != null) params.set('max_price', String(filters.max_price));
    navigate(`/?${params.toString()}`);
  };

  const ChevronBack = isRTL ? ChevronRight : ChevronLeft;

  const renderFilterPills = (f) => {
    const pills = [];
    if (f.query) pills.push({ k: 'q', label: `"${f.query}"` });
    if (f.category) pills.push({ k: 'cat', label: t('savedSearches.pillCategory', { value: f.category }) });
    if (f.location) pills.push({ k: 'loc', label: t('savedSearches.pillLocation', { value: f.location }) });
    if (f.min_price != null || f.max_price != null) {
      const lo = f.min_price != null ? f.min_price : '…';
      const hi = f.max_price != null ? f.max_price : '…';
      pills.push({ k: 'price', label: `${lo} – ${hi}` });
    }
    if (f.hub) pills.push({ k: 'hub', label: f.hub });
    return pills;
  };

  return (
    <div className="saved-searches-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="saved-searches-page">
      <div className="ss-header">
        <button
          className="ss-back-btn"
          onClick={goBack}
          aria-label={t('back')}
          data-testid="ss-back-btn"
        >
          <ChevronBack size={20} />
        </button>
        <div className="ss-title-wrap">
          <Bell size={18} className="ss-title-icon" />
          <h1 className="ss-title">{t('savedSearches.title')}</h1>
        </div>
        <div className="ss-count" data-testid="ss-count">
          {searches.length}/{limit}
        </div>
      </div>

      <p className="ss-subtitle">{t('savedSearches.subtitle')}</p>

      {loading && (
        <div className="ss-empty" data-testid="ss-loading">{t('loading')}…</div>
      )}

      {!loading && searches.length === 0 && (
        <div className="ss-empty" data-testid="ss-empty">
          <Search size={40} className="ss-empty-icon" />
          <p className="ss-empty-title">{t('savedSearches.emptyTitle')}</p>
          <p className="ss-empty-hint">{t('savedSearches.emptyHint')}</p>
        </div>
      )}

      {!loading && searches.length > 0 && (
        <ul className="ss-list" data-testid="ss-list">
          {searches.map(s => (
            <li key={s.id} className="ss-item" data-testid={`ss-item-${s.id}`}>
              <div className="ss-item-main">
                <div className="ss-item-label">
                  {s.label || t('savedSearches.defaultLabel')}
                </div>
                <div className="ss-item-pills">
                  {renderFilterPills(s.filters || {}).map(p => (
                    <span key={p.k} className="ss-pill">{p.label}</span>
                  ))}
                </div>
              </div>
              <div className="ss-item-actions">
                <button
                  className="ss-view-btn"
                  onClick={() => runSearch(s)}
                  data-testid={`ss-view-${s.id}`}
                >
                  {t('savedSearches.viewResults')}
                </button>
                <button
                  className="ss-delete-btn"
                  onClick={() => handleDelete(s.id)}
                  disabled={deletingId === s.id}
                  aria-label={t('savedSearches.delete')}
                  data-testid={`ss-delete-${s.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
